import React from 'react';
import { ClipboardList, FileCheck, Users as UsersIcon, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useReducedMotion, getFadeInAnimation, getScaleAnimation } from '../hooks/useReducedMotion';

const ProcessSection = () => {
  const prefersReducedMotion = useReducedMotion();

  const steps = [
    {
      number: '01',
      icon: ClipboardList,
      title: 'Anfrage & Bedarfsanalyse',
      description: 'Sie kontaktieren uns mit Ihrem Personalbedarf. Wir analysieren Ihre Anforderungen und erstellen ein maßgeschneidertes Konzept.',
      color: 'bg-primary-blue-light text-primary-blue-mid'
    },
    {
      number: '02',
      icon: FileCheck,
      title: 'Angebot & Planung',
      description: 'Sie erhalten ein transparentes Angebot. Nach Ihrer Freigabe planen wir die Details und stellen das perfekte Team zusammen.',
      color: 'bg-primary-skin text-primary-text-dark'
    },
    {
      number: '03',
      icon: UsersIcon,
      title: 'Personaleinsatz',
      description: 'Unsere qualifizierten Mitarbeiter starten den Einsatz. Wir koordinieren alles und sorgen für einen reibungslosen Ablauf vor Ort.',
      color: 'bg-primary-green-light text-green-700'
    },
    {
      number: '04',
      icon: CheckCircle,
      title: 'Abrechnung & Feedback',
      description: 'Nach Abschluss erfolgt die transparente Abrechnung. Wir holen Ihr Feedback ein und optimieren kontinuierlich unsere Leistungen.',
      color: 'bg-primary-blue-light text-primary-blue-mid'
    }
  ];

  const trustElements = [
    {
      title: 'Zertifizierte Mitarbeiter',
      description: 'Alle Mitarbeiter sind geprüft, qualifiziert und zertifiziert',
      badge: '✓ Geprüft'
    },
    {
      title: 'Versicherungsschutz',
      description: 'Vollständiger Versicherungsschutz für alle Einsätze',
      badge: '100%'
    },
    {
      title: 'A1-Bescheinigung',
      description: 'Alle sozialversicherungsrechtlichen Nachweise vorhanden',
      badge: 'EU-weit'
    },
    {
      title: 'DSGVO-konform',
      description: 'Datenschutz nach höchsten europäischen Standards',
      badge: 'Zertifiziert'
    }
  ];

  const certifications = [
    {
      name: 'ISO 9001',
      description: 'Qualitätsmanagement',
      icon: CheckCircle
    },
    {
      name: 'AÜG Lizenz',
      description: 'Arbeitnehmerüberlassung',
      icon: CheckCircle
    },
    {
      name: 'IHK Mitglied',
      description: 'Handelskammer München',
      icon: CheckCircle
    },
    {
      name: 'DSGVO',
      description: 'Datenschutz-Zertifizierung',
      icon: CheckCircle
    }
  ];

  return (
    <section className="section-container bg-gradient-to-b from-white to-primary-skin/20">
      {/* Section Header */}
      <div className="text-center mb-16">
        <h2 className="section-title">
          So funktioniert die Zusammenarbeit
        </h2>
        <p className="section-subtitle">
          Von der ersten Anfrage bis zum erfolgreichen Projektabschluss –
          unser bewährter 4-Schritte-Prozess macht es einfach und transparent.
        </p>
      </div>

      {/* Process Steps */}
      <div className="relative mb-20">
        {/* Connection Line - Desktop */}
        <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-primary-gray-border z-0"></div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10 items-stretch">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              {...getFadeInAnimation(prefersReducedMotion, index * 0.1)}
              className="relative flex flex-col"
            >
              <div className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-hover transition-all duration-300 border border-primary-gray-border h-full flex flex-col">
                {/* Step Number */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-5xl font-bold text-primary-gray-border">
                    {step.number}
                  </span>
                  <div className={`w-14 h-14 rounded-xl ${step.color} flex items-center justify-center`}>
                    <step.icon className="w-7 h-7" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-primary-text-dark mb-3">
                  {step.title}
                </h3>
                <p className="text-sm text-primary-text-muted leading-relaxed flex-grow">
                  {step.description}
                </p>
              </div>

              {/* Arrow for mobile */}
              {index < steps.length - 1 && (
                <div className="lg:hidden flex justify-center my-4">
                  <svg
                    className="w-6 h-6 text-primary-gray-border"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 14l-7 7m0 0l-7-7m7 7V3"
                    />
                  </svg>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Trust Section */}
      <motion.div
        {...getFadeInAnimation(prefersReducedMotion, 0)}
        className="bg-white rounded-2xl p-8 md:p-12 shadow-soft border border-primary-gray-border"
      >
        <div className="text-center mb-10">
          <h3 className="text-2xl md:text-3xl font-bold text-primary-text-dark mb-3">
            Vertrauen durch Sicherheit
          </h3>
          <p className="text-primary-text-muted max-w-2xl mx-auto">
            Wir legen höchsten Wert auf Rechtssicherheit, Compliance und Qualität.
            Alle unsere Prozesse erfüllen die strengsten Standards.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {trustElements.map((element, index) => (
            <motion.div
              key={index}
              {...getScaleAnimation(prefersReducedMotion, index * 0.1)}
              className="text-center p-6 rounded-xl bg-primary-skin/30 hover:bg-primary-blue-light/30 transition-colors duration-300 relative"
            >
              {element.badge && (
                <div className="absolute top-3 right-3 px-2 py-1 bg-green-600 text-white text-xs font-semibold rounded-full">
                  {element.badge}
                </div>
              )}
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-white flex items-center justify-center shadow-sm">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-primary-text-dark mb-2">
                {element.title}
              </h4>
              <p className="text-sm text-primary-text-muted">
                {element.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Certifications Section */}
        <div className="mt-12 pt-12 border-t border-primary-gray-border">
          <h3 className="text-2xl md:text-3xl font-bold text-primary-text-dark mb-8 text-center">
            Zertifizierungen & Partnerschaften
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                {...getScaleAnimation(prefersReducedMotion, index * 0.1)}
                className="text-center p-4 rounded-xl bg-white border-2 border-primary-gray-border hover:border-primary-blue-mid transition-all duration-300"
              >
                <cert.icon className="w-8 h-8 mx-auto mb-2 text-primary-blue-mid" />
                <h5 className="font-bold text-primary-text-dark text-sm mb-1">
                  {cert.name}
                </h5>
                <p className="text-xs text-primary-text-muted">
                  {cert.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default ProcessSection;
